import { motion } from "framer-motion";
import { Play, ExternalLink, Newspaper } from "lucide-react";

const videos = [
  {
    id: "video-1",
    title: "Mandi Trades - Introduction",
    description: "Learn about Mandi Trades and how it's transforming agricultural commerce.",
    youtubeId: "kDUYt6C5qjQ",
  },
  {
    id: "video-2",
    title: "Mandi Trades - Platform Overview",
    description: "A comprehensive overview of the Mandi Trades platform and its features.",
    youtubeId: "EJaSjrWraQ4",
  },
];

const pressCoverage = [
  {
    id: "press-1",
    title: "Mandi Trades: Sowing the seeds of genuine profit",
    source: "Livemint",
    url: "https://www.livemint.com/Companies/yW1LDUCfWZDnnGYAPj1ddJ/Mandi-Trades-Sowing-the-seeds-of-genuine-profit.html",
    image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&q=80"
  },
  {
    id: "press-2",
    title: "Harnessing power of latest technology trends in Mandi Trades",
    source: "Economic Times",
    url: "https://economictimes.indiatimes.com/we-are-harnessing-power-of-all-the-latest-technology-trends-in-mandi-trades-app/changemakersshow/48655080.cms",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80"
  },
  {
    id: "press-3",
    title: "An app that helps farmers cut the middleman out",
    source: "Livemint",
    url: "https://www.livemint.com/Consumer/nQLEyDHTQvkVAodbfA6B9L/An-app-that-helps-farmers-cut-the-middleman-out.html",
    image: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=600&q=80"
  },
  {
    id: "press-4",
    title: "Mandi Trades - Official Documentation",
    source: "Data.gov.in",
    url: "https://www.data.gov.in/sites/default/files/MandiTrades.pdf",
    image: "https://images.unsplash.com/photo-1586771107445-d3ca888129ff?w=600&q=80"
  },
];

const VideosPage = () => {
  return (
    <div data-testid="videos-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#2997FF] text-sm font-medium tracking-widest uppercase mb-4">Media</p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-[#F5F5F7] tracking-tight mb-6" data-testid="videos-title">
              Videos
            </h1>
            <p className="text-lg md:text-xl text-[#86868B] max-w-3xl mx-auto">
              Watch our videos to learn more about Mandi Trades and our solutions.
            </p>
          </motion.div>

          {/* Videos Section */}
          <div className="mb-20">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-2xl md:text-3xl font-semibold text-[#F5F5F7] mb-8 flex items-center gap-3"
            >
              <Play size={28} className="text-[#2997FF]" />
              Featured Videos
            </motion.h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="service-card overflow-hidden"
                  data-testid={`video-${video.id}`}
                >
                  {/* YouTube Embed */}
                  <div className="relative w-full aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.youtubeId}`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                  
                  {/* Video Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#F5F5F7] mb-2">
                      {video.title}
                    </h3>
                    <p className="text-[#86868B]">{video.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Press Coverage Section */}
      <section className="py-24 md:py-32 bg-[#1C1C1E]">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl font-semibold text-[#F5F5F7] mb-4 flex items-center gap-3"
          >
            <Newspaper size={28} className="text-[#2997FF]" />
            Press Coverage
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[#86868B] mb-10 max-w-2xl"
          >
            Read about Mandi Trades in leading publications and official documentation.
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {pressCoverage.map((item, index) => (
              <motion.a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3]"
                data-testid={`press-${item.id}`}
              >
                {/* Image */}
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-[#2997FF] text-white text-xs font-medium rounded-full">
                      {item.source}
                    </span>
                    <ExternalLink size={14} className="text-white/70 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <h3 className="text-lg font-semibold text-white line-clamp-2 group-hover:text-[#2997FF] transition-colors">
                    {item.title}
                  </h3>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* More Content Coming */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F5F5F7] tracking-tight mb-4">
              More content coming soon
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              We're working on new video content showcasing our latest projects and solutions.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VideosPage;
